import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Flex, Text, Icon, VStack, Image, Tooltip, IconButton, HStack } from "@chakra-ui/react";
import { FaPlay, FaPause, FaRedo, FaChevronRight, FaBackward, FaForward, FaEye, FaEyeSlash } from "react-icons/fa";

const demoSteps = [
  {
    id: "intro",
    title: "Transforma EditLaTeX",
    text: "A modern, easy-to-use cloud platform changing how publishers, editors, and authors work together to build flawless publications.",
    image: "/images/white-logo.png",
    isHero: true,
    duration: 8000
  },
  {
    id: "signup",
    title: "Quick & Easy Access",
    features: [
      { bold: "Cloud-Based Platform", desc: "No complex LaTeX software installations required.", image: "/images/video/CloudBased.png" },
      { bold: "Instant Sign Up", desc: "Get straight to your dashboard in seconds.", image: "/images/video/SignUp.png" },
      { bold: "Secure Authentication", desc: "Enterprise-grade security protecting your work.", image: "/images/video/SignIn.png" }
    ],
    image: "/images/presentation/placeholder_signup.png",
    duration: 10000
  },
  {
    id: "create_project",
    title: "Start Your First Project",
    features: [
      { bold: "One-Click Creation", desc: "Create a new LaTeX project from your dashboard instantly.", image: "/images/video/CreateProject.png" },
      { bold: "Apply Custom Templates", desc: "Link your project to existing styles and layouts.", image: "/images/video/ApplyTemplate.png" },
      { bold: "Organized Dashboard", desc: "Manage all your publications and files in one place.", image: "/images/video/Dashboard.png" }
    ],
    image: "/images/presentation/placeholder_create.png",
    duration: 11000
  },
  {
    id: "upload_file",
    title: "Seamless File Uploads",
    features: [
      { bold: "Drag & Drop Support", desc: "Easily upload multiple files directly into the workspace.", image: "/images/video/UploadFiles.png" },
      { bold: "Broad Compatibility", desc: "Supports .tex, .bib, .cls, .sty, .txt, and image formats (.jpg, .png, .eps).", image: "/images/video/MoreFileFormates.png" },
      { bold: "Automatic File Indexing", desc: "Mark your main file and we handle the rest.", image: "/images/video/AutoFileIndex.png" }
    ],
    image: "/images/presentation/placeholder_upload.png",
    duration: 11000
  },
  {
    id: "workspace",
    title: "The Editor's Workspace",
    features: [
      { bold: "All-In-One View", desc: "See the LaTeX code, live PDF preview, and queries side-by-side.", image: "/images/video/AllInOneView.png" },
      { bold: "Powerful Search & Replace", desc: "Use advanced Regex to fix styling issues across the document.", image: "/images/video/SearchAndReplace.png" },
      { bold: "Clear Error Messages", desc: "Points you directly to the exact line of code needing fixes.", image: "/images/video/ErrorShow.png" }
    ],
    image: "/images/presentation/three_pane.png",
    duration: 11000
  },
  {
    id: "environment",
    title: "Templates & Environments",
    features: [
      { bold: "Reusable Assets", desc: "Create your own 'Environments' to store common bibliographies and styles.", image: "/images/video/CreateOwnEnvironment.png" },
      { bold: "Consistent Branding", desc: "Apply the same environment across multiple projects for uniformity.", image: "/images/video/ApplyTemplate.png" },
      { bold: "Browse Templates", desc: "Select from pre-configured journal and book templates.", image: "/images/video/BrowseTemplate.png" }
    ],
    image: "/images/presentation/placeholder_environment.png",
    duration: 11000
  },
  {
    id: "shortcuts",
    title: "Powerful Macros & Shortcuts",
    features: [
      { bold: "Smart Auto-Casing", desc: "Use Alt+Shift+x to instantly format sentences to standard title case.", image: "/images/video/TitleCase.png" },
      { bold: "Quick Creation", desc: "Press Ctrl+F2 to instantly spawn a new LaTeX file.", image: "/images/video/QuickCreation.png" },
      { bold: "Instant Snippets", desc: "Built-in macros for \\textbf, \\textit, \\cmidrule, and more.", image: "/images/video/InstantSnippets.png" }
    ],
    image: "/images/video/placeholder_shortcuts.png",
    duration: 11000
  },
  {
    id: "compile",
    title: "Smart Compilation",
    features: [
      { bold: "Dynamic Compilers", desc: "Automatically switches between PDFLaTeX, XeLaTeX, and LuaLaTeX.", image: "/images/video/Compiler.png" },
      { bold: "Auto-Compile Engine", desc: "Compiles your document seamlessly in isolated Docker sandboxes.", image: "/images/video/AutoCompile.png" },
      { bold: "Instant Math Previews", desc: "Preview complex equations visually before running a full build.", image: "/images/video/InstantMathPreview.png" }
    ],
    image: "/images/presentation/math.png",
    duration: 11000
  },
  {
    id: "collab",
    title: "Real-Time Collaboration",
    features: [
      { bold: "True Teamwork", desc: "Authors and multiple editors can type concurrently on the same file.", image: "/images/video/TeamWork.png" },
      { bold: "Live Cursors", desc: "See colored cursors and names jumping around the screen.", image: "/images/video/LiveCursor.png" },
      { bold: "Safe Merging", desc: "High-speed caching ensures no work is ever overwritten or lost.", image: "/images/video/LiveCursor.png" }
    ],
    image: "/images/presentation/collab.png",
    duration: 11000
  },
  {
    id: "publisher",
    title: "Built for Publishers",
    features: [
      { bold: "One-Click Downloads", desc: "Get the final PDF, LaTeX source, and logs with one button.", image: "/images/video/Download.png" },
      { bold: "Automatic XML Generation", desc: "Builds perfect digital XML text for websites and eReaders.", image: "/images/video/XMLGeneration.png" },
      { bold: "Auto-Pagination", desc: "Builds perfect PDF pages automatically without manual help.", image: "/images/video/AutoPagination.png" }
    ],
    image: "/images/presentation/download.png",
    duration: 11000
  },
  {
    id: "proofing",
    title: "Author Proofing & Comments",
    features: [
      { bold: "Direct Comments", desc: "Leave sticky notes and questions attached directly to a sentence.", image: "/images/video/InstantFeedBack.png" },
      { bold: "Instant Feedback", desc: "When authors answer a question, the change is verified instantly.", image: "/images/video/InstantFeedBack.png" },
      { bold: "Status Dashboard", desc: "Track exactly how many questions are left open before printing.", image: "/images/video/InstantFeedBack.png" }
    ],
    image: "/images/presentation/author.png",
    duration: 11000
  },
  {
    id: "sync",
    title: "Click the PDF, Find the Code",
    features: [
      { bold: "SyncTeX Integration", desc: "Mathematically links the text on the PDF back to the raw code.", image: "/images/video/TextToPDFJump.png" },
      { bold: "Fix Typos Instantly", desc: "Click a word on the PDF, and your cursor jumps to that line!", image: "/images/video/FixTypoInstantly.png" },
      { bold: "No More Hunting", desc: "Never manually scroll through thousands of lines of code again.", image: "/images/video/TextToPDFJump.png" }
    ],
    image: "/images/presentation/toc.png",
    duration: 11000
  },
  {
    id: "pipeline",
    title: "The EditLaTeX Automatic Pipeline",
    features: [
      { bold: "1. Check & Structure", desc: "Automatically check files and safely organize messy templates." },
      { bold: "2. Clean & Merge", desc: "Force standard formatting and organize bibliography citations." },
      { bold: "3. Build & Deliver", desc: "Generate XML, auto-paginate the PDF, and finish projects perfectly." }
    ],
    isPipeline: true,
    duration: 12000
  },
  {
    id: "outro",
    title: "Thank You",
    text: "Ready to experience the future of digital typesetting? Let's build something incredible together.",
    image: "/images/white-logo.png",
    isHero: true,
    duration: 10000
  }
];

const TotalDuration = demoSteps.reduce((acc, step) => acc + step.duration, 0);

// Helper to determine feature relative progress and timing
const getFeatureTiming = (featureIndex, features, stepDuration, timeInStep) => {
  const numFeatures = features.length;
  if (numFeatures === 0) return { isActive: false, progressPercent: 0, isPast: false };

  const startOffset = 800;
  const availableDuration = stepDuration - startOffset;
  const featureDuration = availableDuration / numFeatures;

  const start = startOffset + featureIndex * featureDuration;
  const end = start + featureDuration;

  const isActive = timeInStep >= start && timeInStep < end;
  const isPast = timeInStep >= end;

  let progressPercent = 0;
  if (isPast) {
    progressPercent = 100;
  } else if (isActive) {
    progressPercent = ((timeInStep - start) / featureDuration) * 100;
  }

  return { isActive, progressPercent, isPast };
};

const getActiveFeatureIndex = (features, stepDuration, timeInStep) => {
  const numFeatures = features.length;
  if (numFeatures === 0) return -1;

  const startOffset = 800;
  if (timeInStep < startOffset) return -1;

  const availableDuration = stepDuration - startOffset;
  const featureDuration = availableDuration / numFeatures;

  const calculatedIdx = Math.floor((timeInStep - startOffset) / featureDuration);
  return Math.min(numFeatures - 1, Math.max(0, calculatedIdx));
};

const ProductDemoVideo = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [showControls, setShowControls] = useState(true);

  // Playback accumulation loop matching speed selectors
  useEffect(() => {
    let timer;
    if (isPlaying) {
      timer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= TotalDuration) {
            clearInterval(timer);
            return TotalDuration;
          }
          return Math.min(TotalDuration, prev + 50 * playbackSpeed);
        });
      }, 50);
    }
    return () => clearInterval(timer);
  }, [isPlaying, playbackSpeed]);

  useEffect(() => {
    if (progress === TotalDuration) {
      setIsPlaying(false);
    }

    let accumulatedTime = 0;
    for (let i = 0; i < demoSteps.length; i++) {
      accumulatedTime += demoSteps[i].duration;
      if (progress < accumulatedTime) {
        if (currentStepIndex !== i) {
          setCurrentStepIndex(i);
        }
        break;
      }
    }
  }, [progress, currentStepIndex]);

  const togglePlay = () => {
    if (progress >= TotalDuration) {
      setProgress(0);
      setCurrentStepIndex(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    setProgress(percentage * TotalDuration);
  };

  const jumpToStep = (idx) => {
    const targetProgress = demoSteps.slice(0, idx).reduce((acc, s) => acc + s.duration, 0);
    setProgress(targetProgress);
    setCurrentStepIndex(idx);
    setIsPlaying(true);
  };

  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      jumpToStep(currentStepIndex - 1);
    }
  };

  const handleNextStep = () => {
    if (currentStepIndex < demoSteps.length - 1) {
      jumpToStep(currentStepIndex + 1);
    }
  };

  const currentStep = demoSteps[currentStepIndex];
  const timeInStep = progress - demoSteps.slice(0, currentStepIndex).reduce((acc, s) => acc + s.duration, 0);

  // Staggered activeImage and activeIdx calculations matching timing boundaries perfectly
  const activeIdx = getActiveFeatureIndex(currentStep.features || [], currentStep.duration, timeInStep);
  const activeImage = currentStep.features && activeIdx >= 0 && currentStep.features[activeIdx] && currentStep.features[activeIdx].image
    ? currentStep.features[activeIdx].image
    : currentStep.image;

  useEffect(() => {
    setIsImageLoaded(false);
  }, [activeImage, currentStepIndex]);

  return (
    <Box w="100vw" h="100vh" bg="var(--bg)" overflow="hidden" position="relative" fontFamily="inherit" color="var(--text-primary)">
      
      {/* Full-Screen Cinematic Transitional Swipe Wash */}
      <AnimatePresence>
        <motion.div
          key={`swipe-${currentStepIndex}`}
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "var(--gradient-primary)",
            zIndex: 45,
            pointerEvents: "none",
            opacity: 0.12
          }}
        />
      </AnimatePresence>

      {/* Floating Cinematic Background Light Leaks */}
      <Box position="absolute" inset={0} overflow="hidden" pointerEvents="none" zIndex={2}>
        {[
          { size: "160px", color: "var(--primary)", top: "15%", left: "12%", duration: 18, delay: 0 },
          { size: "230px", color: "var(--btn-bg)", top: "62%", left: "72%", duration: 25, delay: 2 },
          { size: "130px", color: "var(--primary)", top: "8%", left: "82%", duration: 20, delay: 4 },
        ].map((l, i) => (
          <motion.div
            key={i}
            style={{
              position: "absolute",
              top: l.top,
              left: l.left,
              width: l.size,
              height: l.size,
              borderRadius: "full",
              background: l.color,
              filter: "blur(100px)",
              opacity: 0.05
            }}
            animate={{
              x: [0, 35, -35, 0],
              y: [0, -25, 25, 0],
              opacity: [0.03, 0.06, 0.03]
            }}
            transition={{
              duration: l.duration,
              repeat: Infinity,
              delay: l.delay,
              ease: "easeInOut"
            }}
          />
        ))}
      </Box>
      
      {/* Drifting Ambient Background Glows */}
      <motion.div 
        animate={{
          scale: [1, 1.12, 1],
          x: [0, 25, 0],
          y: [0, -15, 0]
        }}
        transition={{
          duration: 24,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: "absolute",
          top: "-30%",
          left: "-20%",
          width: "70%",
          height: "70%",
          backgroundColor: "var(--primary)",
          filter: "blur(250px)",
          opacity: 0.12,
          borderRadius: "full",
          pointerEvents: "none"
        }}
      />
      <motion.div 
        animate={{
          scale: [1, 1.08, 1],
          x: [0, -25, 0],
          y: [0, 30, 0]
        }}
        transition={{
          duration: 28,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: "absolute",
          bottom: "-30%",
          right: "-20%",
          width: "70%",
          height: "70%",
          backgroundColor: "var(--btn-bg)",
          filter: "blur(250px)",
          opacity: 0.12,
          borderRadius: "full",
          pointerEvents: "none"
        }}
      />

      {/* Main Video Content Area */}
      <Flex w="100%" h="100%" alignItems="center" justifyContent="center" position="relative" zIndex={10} pb={{ base: "90px", md: "110px" }}>

        <Box w="100%" h="100%" maxW="1920px" position="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep.id}
              initial={{ opacity: 0, filter: "blur(8px)", scale: 1.01 }}
              animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
              exit={{ opacity: 0, filter: "blur(8px)", scale: 0.99 }}
              transition={{ duration: 1.0, ease: "easeInOut" }}
              style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
            >
              {currentStep.isHero ? (
                <Flex direction="column" alignItems="center" justifyContent="center" h="100%" textAlign="center" px={4}>
                  <motion.div
                    animate={{
                      y: [0, -8, 8, 0]
                    }}
                    transition={{
                      duration: 10,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.93 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 1.4, ease: "easeOut" }}
                    >
                      {currentStep.image && (
                        <Image src={process.env.PUBLIC_URL + currentStep.image} alt="Logo" maxH="150px" mb={10} style={{ filter: "drop-shadow(0 0 20px var(--primary))" }} />
                      )}
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 25 }}
                      animate={timeInStep > 1200 ? { opacity: 1, y: 0 } : { opacity: 0, y: 25 }}
                      transition={{ type: "spring", stiffness: 80, damping: 15 }}
                    >
                      <Text fontSize={{ base: "4xl", md: "7xl" }} fontWeight="900" bgGradient="var(--gradient-primary)" bgClip="text" mb={8} letterSpacing="tight">
                        {currentStep.title}
                      </Text>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 25 }}
                      animate={timeInStep > 2600 ? { opacity: 1, y: 0 } : { opacity: 0, y: 25 }}
                      transition={{ type: "spring", stiffness: 80, damping: 15 }}
                    >
                      <Text fontSize={{ base: "xl", md: "3xl" }} color="var(--text-secondary)" maxW="900px" lineHeight="1.6" fontWeight="medium" opacity={0.9}>
                        {currentStep.text}
                      </Text>
                    </motion.div>
                  </motion.div>
                </Flex>
              ) : currentStep.isPipeline ? (
                <Flex direction="column" alignItems="center" justifyContent="center" h="100%" px={8}>
                  <Text fontSize={{ base: "3xl", md: "5xl" }} fontWeight="900" mb={16} color="var(--text-primary)">{currentStep.title}</Text>
                  <Flex w="100%" maxW="1400px" justifyContent="space-between" alignItems="stretch" position="relative" direction={{ base: "column", lg: "row" }} gap={8}>
                    {/* Background inactive line */}
                    <Box display={{ base: "none", lg: "block" }} position="absolute" top="40%" left="5%" right="5%" h="3px" bg="var(--border)" zIndex={0} opacity={0.2} />
                    {/* Dynamic drawing connecting line */}
                    {(() => {
                      const numFeatures = currentStep.features.length;
                      const activeFeaturesCount = currentStep.features.filter((_, idx) => timeInStep > (1000 + idx * 2500)).length;
                      const filledWidth = numFeatures > 1 ? Math.min(100, Math.max(0, (activeFeaturesCount - 1) / (numFeatures - 1) * 90)) : 0;
                      
                      return (
                        <motion.div 
                          initial={{ width: "0%" }}
                          animate={{ width: `${filledWidth}%` }}
                          transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
                          style={{
                            position: "absolute",
                            top: "40%",
                            left: "5%",
                            height: "3px",
                            background: "var(--gradient-primary)",
                            zIndex: 0,
                            opacity: 0.6
                          }}
                        />
                      );
                    })()}

                    {currentStep.features.map((feature, idx) => {
                      const isVisible = timeInStep > (1000 + idx * 2500);
                      const isActive = timeInStep >= (1000 + idx * 2500) && (idx === currentStep.features.length - 1 || timeInStep < (1000 + (idx + 1) * 2500));

                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 30 }}
                          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          style={{ flex: 1, display: "flex", width: "100%", zIndex: 1 }}
                        >
                          <VStack
                            bg="var(--card-bg)"
                            backdropFilter="blur(16px)"
                            p={8}
                            borderRadius="2xl"
                            border="1px solid"
                            borderColor={isActive ? "var(--primary)" : "var(--border)"}
                            w="100%"
                            boxShadow="var(--shadow-lg)"
                            style={{
                              transform: isActive ? "scale(1.03)" : "scale(1)",
                              opacity: isActive ? 1 : isVisible ? 0.65 : 0.25,
                              transition: "all 0.3s ease-in-out"
                            }}
                          >
                            <Flex w="80px" h="80px" borderRadius="full" bgGradient="var(--gradient-primary)" alignItems="center" justifyContent="center" mb={6} boxShadow={isActive ? "var(--shadow-glow)" : "none"} style={{ transition: "all 0.3s" }}>
                              <Icon as={FaChevronRight} color="var(--btn-text)" boxSize={6} />
                            </Flex>
                            <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={4} color="var(--text-primary)">{feature.bold}</Text>
                            <Text color="var(--text-secondary)" textAlign="center" fontSize="md" lineHeight="1.7">{feature.desc}</Text>
                          </VStack>
                        </motion.div>
                      );
                    })}
                  </Flex>
                </Flex>
              ) : (
                <Flex h="100%" w="100%" direction={{ base: "column", lg: "row" }} alignItems="center" justifyContent="space-between" px={{ base: 6, md: 10, lg: 12 }} py={6} gap={{ base: 8, lg: 10 }}>
                  
                  {/* Left Column: Content Text & Features with dynamic highlight timer lines */}
                  <Box w={{ base: "100%", lg: "35%" }} maxW="480px" zIndex={2}>
                    <Text fontSize={{ base: "4xl", md: "5xl" }} fontWeight="900" mb={8} bgGradient="var(--gradient-primary)" bgClip="text" letterSpacing="tight" lineHeight="1.2">
                      {currentStep.title}
                    </Text>
                    <VStack align="flex-start" spacing={6} w="100%">
                      {currentStep.features.map((feature, idx) => {
                        const timing = getFeatureTiming(idx, currentStep.features, currentStep.duration, timeInStep);
                        
                        // A topic is visible if the playback time has reached its dynamic start boundary!
                        const numFeatures = currentStep.features.length;
                        const startOffset = 800;
                        const availableDuration = currentStep.duration - startOffset;
                        const featureDuration = availableDuration / numFeatures;
                        const featureStart = startOffset + idx * featureDuration;
                        const isVisible = timeInStep >= featureStart;

                        return (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -30 }}
                            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                            transition={{ type: "spring", stiffness: 90, damping: 15 }}
                            style={{ width: "100%" }}
                          >
                            <Box
                              p={3.5}
                              borderRadius="xl"
                              border="1px solid"
                              borderColor={timing.isActive ? "rgba(147, 137, 255, 0.2)" : "transparent"}
                              bg={timing.isActive ? "rgba(255, 255, 255, 0.02)" : "transparent"}
                              style={{
                                opacity: timing.isActive ? 1 : timing.isPast ? 0.65 : 0.35,
                                transform: timing.isActive ? "scale(1.02) translateX(4px)" : "scale(1)",
                                transition: "all 0.3s ease-in-out"
                              }}
                            >
                              <Flex align="flex-start">
                                <Box 
                                  mt={1} 
                                  mr={4} 
                                  p={1.5} 
                                  bg={timing.isActive ? "var(--primary)" : "var(--btn-bg)"} 
                                  borderRadius="full"
                                  color="var(--btn-text)"
                                  style={{ transition: "all 0.3s" }}
                                >
                                  <Icon as={FaChevronRight} boxSize={2.5} display="block" />
                                </Box>
                                <Box flex={1}>
                                  <Text fontSize="xl" fontWeight="bold" mb={1.5} color={timing.isActive ? "var(--text-primary)" : "var(--text-secondary)"} style={{ transition: "all 0.3s" }}>
                                    {feature.bold}
                                  </Text>
                                  <Text fontSize="lg" color="var(--text-secondary)" lineHeight="1.5">
                                    {feature.desc}
                                  </Text>
                                  
                                  {/* Staggered progress bar timer for current active index */}
                                  {timing.isActive && (
                                    <Box w="100%" h="2px" bg="rgba(255, 255, 255, 0.08)" mt={3} borderRadius="full" overflow="hidden">
                                      <motion.div
                                        initial={{ width: "0%" }}
                                        animate={{ width: `${timing.progressPercent}%` }}
                                        transition={{ duration: 0.05, ease: "linear" }}
                                        style={{ height: "100%", backgroundColor: "var(--primary)" }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Flex>
                            </Box>
                          </motion.div>
                        );
                      })}
                    </VStack>
                  </Box>

                  {/* Right Column: Image Preview - Transparent, borderless container for clean floating image */}
                  <Box
                    w={{ base: "100%", lg: "60%" }}
                    h={{ base: "320px", md: "480px", lg: "70vh" }}
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                    overflow="hidden"
                  >
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={activeImage}
                        src={process.env.PUBLIC_URL + activeImage}
                        alt={currentStep.title}
                        style={{
                          position: "absolute",
                          objectFit: "contain",
                          width: "100%",
                          height: "100%",
                          maxWidth: "100%",
                          maxHeight: "100%",
                        }}
                        initial={{ opacity: 0, x: 45 }}
                        animate={{ 
                          opacity: isImageLoaded ? 1 : 0, 
                          x: isImageLoaded ? 0 : 45
                        }}
                        exit={{ opacity: 0, x: -45 }}
                        transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
                        onLoad={() => setIsImageLoaded(true)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/1920x1080/1a202c/ffffff?text=Image+Placeholder+(Add+in+Future)";
                          setIsImageLoaded(true);
                        }}
                      />
                    </AnimatePresence>
                  </Box>

                </Flex>
              )}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Flex>

      {/* Tiny Eye Show/Hide Toggle Button */}
      <Box position="absolute" bottom="24px" right="24px" zIndex={35}>
        <Tooltip label={showControls ? "Hide Controls (Cinematic Mode)" : "Show Controls"} placement="top">
          <IconButton
            size="md"
            variant="solid"
            bg="var(--card-bg)"
            color="var(--text-primary)"
            border="1px solid"
            borderColor="var(--border)"
            _hover={{ bg: "var(--primary)", color: "#fff" }}
            icon={showControls ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            onClick={() => setShowControls(!showControls)}
            aria-label="Toggle Cockpit Controls"
            borderRadius="full"
            boxShadow="var(--shadow-lg)"
          />
        </Tooltip>
      </Box>

      {/* Modern Floating Video Controls Footer Cockpit */}
      <motion.div
        initial={{ y: 0, opacity: 1 }}
        animate={showControls ? { y: 0, opacity: 1 } : { y: 140, opacity: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 16 }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          padding: "24px",
          zIndex: 30,
          pointerEvents: showControls ? "auto" : "none"
        }}
      >
        <Flex maxW="1600px" mx="auto" alignItems="center" gap={6} bg="var(--card-bg)" backdropFilter="blur(20px)" p={4} borderRadius="2xl" border="1px solid" borderColor="var(--border)" boxShadow="var(--shadow-md)">
          
          {/* Navigation Deck skips */}
          <HStack spacing={1}>
            <Tooltip label="Previous Chapter" placement="top">
              <IconButton
                size="sm"
                variant="ghost"
                color="var(--text-primary)"
                _hover={{ bg: "rgba(255, 255, 255, 0.05)" }}
                icon={<FaBackward />}
                onClick={handlePrevStep}
                isDisabled={currentStepIndex === 0}
                aria-label="Previous step"
              />
            </Tooltip>

            <Box cursor="pointer" onClick={togglePlay} color="var(--text-primary)" _hover={{ color: "var(--primary)", transform: "scale(1.1)" }} transition="all 0.2s" display="flex" alignItems="center" justifyContent="center" w={10} h={10}>
              {progress >= TotalDuration ? <FaRedo size={22} /> : isPlaying ? <FaPause size={22} /> : <FaPlay size={22} />}
            </Box>

            <Tooltip label="Next Chapter" placement="top">
              <IconButton
                size="sm"
                variant="ghost"
                color="var(--text-primary)"
                _hover={{ bg: "rgba(255, 255, 255, 0.05)" }}
                icon={<FaForward />}
                onClick={handleNextStep}
                isDisabled={currentStepIndex === demoSteps.length - 1}
                aria-label="Next step"
              />
            </Tooltip>
          </HStack>

          {/* Time Seek Scrub bar with glowing runner pin */}
          <Box flex={1} position="relative" h="20px" display="flex" alignItems="center" cursor="pointer" onClick={handleSeek}>
            {/* Scrubber progress track */}
            <Box flex={1} position="relative" h="8px" bg="var(--border)" borderRadius="full" overflow="hidden">
              <Box position="absolute" top={0} left={0} h="100%" bg="var(--primary)" borderRadius="full" width={`${(progress / TotalDuration) * 100}%`} transition="width 0.05s linear" />

              {demoSteps.map((step, idx) => {
                if (idx === 0) return null;
                const prevDuration = demoSteps.slice(0, idx).reduce((acc, s) => acc + s.duration, 0);
                const leftPercent = (prevDuration / TotalDuration) * 100;
                return (
                  <Box
                    key={idx}
                    position="absolute"
                    top={0}
                    left={`${leftPercent}%`}
                    w="2px"
                    h="100%"
                    bg="var(--bg)"
                    opacity={0.35}
                    transform="translateX(-50%)"
                  />
                );
              })}
            </Box>

            {/* Glowing scrubber node indicator */}
            <Box 
              position="absolute" 
              left={`${(progress / TotalDuration) * 100}%`} 
              w="14px" 
              h="14px" 
              bg="var(--primary)" 
              borderRadius="full" 
              boxShadow="0 0 10px var(--primary)"
              transform="translateX(-50%)"
              pointerEvents="none"
              style={{ transition: "left 0.05s linear" }}
            />
          </Box>

          {/* Playback speed selector */}
          <HStack bg="rgba(255, 255, 255, 0.04)" p={0.5} borderRadius="lg" border="1px solid" borderColor="var(--border)">
            {[0.5, 1, 1.5, 2].map((s) => (
              <Box
                key={s}
                onClick={() => setPlaybackSpeed(s)}
                px={2.5}
                py={1}
                borderRadius="md"
                cursor="pointer"
                bg={playbackSpeed === s ? "var(--primary)" : "transparent"}
                color={playbackSpeed === s ? "#fff" : "var(--text-secondary)"}
                fontSize="11px"
                fontWeight="bold"
                transition="all 0.2s"
              >
                {s}x
              </Box>
            ))}
          </HStack>

          <Text fontSize="md" color="var(--text-primary)" fontWeight="600" w="130px" textAlign="right" fontFamily="monospace" letterSpacing="wide">
            {Math.floor(progress / 1000 / 60)}:{(Math.floor(progress / 1000) % 60).toString().padStart(2, '0')} / {Math.floor(TotalDuration / 1000 / 60)}:{(Math.floor(TotalDuration / 1000) % 60).toString().padStart(2, '0')}
          </Text>
        </Flex>
      </motion.div>
    </Box>
  );
};

export default ProductDemoVideo;
